import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, RefreshCw, Wand2, Search, FileText, Mail, Link2,
  Trash2, CheckCircle2, XCircle, ExternalLink, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  API, redesignDiscover, redesignGenerate, redesignJobs, redesignDelete,
  redesignLink, redesignEmail,
} from "@/lib/api";

const publicPdfUrl = (path, token) =>
  `${API.replace(/\/api$/, "")}${path}?token=${token}`;

const StatusBadge = ({ status }) => {
  const map = {
    done: ["text-emerald-700 bg-emerald-50", CheckCircle2, "Ready"],
    error: ["text-rose-700 bg-rose-50", XCircle, "Failed"],
    running: ["text-blue-700 bg-blue-50", Loader2, "Generating"],
    queued: ["text-amber-700 bg-amber-50", Clock, "Queued"],
  };
  const [cls, Icon, label] = map[status] || map.queued;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      <Icon size={12} className={status === "running" ? "animate-spin" : ""} /> {label}
    </span>
  );
};

export default function AdminRedesign() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [url, setUrl] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [discovery, setDiscovery] = useState(null);
  const [selected, setSelected] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState(null);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const loadJobs = useCallback(() => {
    redesignJobs().then((d) => setJobs(d.jobs || [])).catch(() => setJobs([]));
  }, []);
  useEffect(() => { if (user?.role === "admin") loadJobs(); }, [user, loadJobs]);

  // Poll while any job is running/queued
  useEffect(() => {
    if (!jobs) return;
    const active = jobs.some((j) => j.status === "running" || j.status === "queued");
    if (!active) return;
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);

  const discover = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setDiscovering(true); setDiscovery(null); setSelected({});
    try {
      const d = await redesignDiscover(url.trim());
      setDiscovery(d);
      const pre = {};
      (d.pages || []).forEach((p) => { if (p.recommended || p.path === "/") pre[p.url] = true; });
      setSelected(pre);
    } catch {
      toast.error("Couldn't read that website. Check the URL.");
    }
    setDiscovering(false);
  };

  const toggle = (u) => setSelected((s) => ({ ...s, [u]: !s[u] }));
  const selectedUrls = Object.keys(selected).filter((u) => selected[u]);

  const generate = async () => {
    if (!selectedUrls.length) { toast.error("Select at least one page."); return; }
    if (selectedUrls.length > 8) { toast.error("Please select up to 8 pages per deck."); return; }
    setGenerating(true);
    try {
      await redesignGenerate({
        url: url.trim(), pages: selectedUrls,
        client_name: clientName, client_email: clientEmail, notes,
      });
      toast.success("Redesign deck is generating — it'll appear below.");
      setDiscovery(null); setSelected({}); setUrl(""); setClientName(""); setClientEmail(""); setNotes("");
      loadJobs();
    } catch {
      toast.error("Couldn't start the redesign job.");
    }
    setGenerating(false);
  };

  const openPdf = async (id) => {
    try {
      const { token, path } = await redesignLink(id);
      window.open(publicPdfUrl(path, token), "_blank");
    } catch { toast.error("Deck not ready yet."); }
  };

  const copyLink = async (id) => {
    try {
      const { token, path } = await redesignLink(id);
      await navigator.clipboard.writeText(publicPdfUrl(path, token));
      toast.success("Shareable link copied.");
    } catch { toast.error("Couldn't get link."); }
  };

  const emailDeck = async (id) => {
    try {
      const r = await redesignEmail(id);
      toast.success(r.message || "Proposal emailed.");
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "This proposal has no valid client email.");
    }
  };

  const del = async (id, domain) => {
    if (!window.confirm(`Delete redesign deck for ${domain}?`)) return;
    try { await redesignDelete(id); toast.success("Deleted"); loadJobs(); } catch { toast.error("Delete failed"); }
  };

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-redesign">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand"><ArrowLeft size={16} /> Console</Link>
        <span className="text-sm font-semibold text-slate-700">Full Site Redesign Decks</span>
        <button onClick={loadJobs} className="text-slate-400 hover:text-brand" data-testid="redesign-refresh"><RefreshCw size={16} /></button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><Wand2 className="text-brand" size={22} /> AI Redesign Proposal Generator</h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">Analyze any prospect's website, pick the pages that matter, and let AI generate a full page-by-page redesign with rewritten copy — bundled into one branded PDF you can email or share.</p>

        {/* Step 1 — discover */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow mt-6">
          <form onSubmit={discover} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Prospect website URL, e.g. litlab.com" data-testid="redesign-url-input"
                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand" />
            </div>
            <button type="submit" disabled={discovering} data-testid="redesign-discover-btn"
              className="bg-brand text-white font-semibold rounded-lg px-6 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {discovering ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Analyze site
            </button>
          </form>
        </div>

        {/* Step 2 — select pages + client details */}
        {discovery && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow mt-4" data-testid="redesign-page-picker">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-slate-800">{discovery.domain}</p>
                <p className="text-xs text-slate-400">{discovery.pages?.length || 0} pages found · {discovery.recommended_count} recommended · {selectedUrls.length} selected (max 8)</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-2 mt-4 max-h-72 overflow-y-auto pr-1">
              {(discovery.pages || []).map((p) => (
                <label key={p.url} data-testid="redesign-page-option"
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selected[p.url] ? "border-brand bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="checkbox" checked={!!selected[p.url]} onChange={() => toggle(p.url)} className="mt-0.5 accent-brand" />
                  <span className="min-w-0">
                    <span className="text-sm font-medium text-slate-800 block truncate">{p.label || p.path}</span>
                    <span className="text-xs text-slate-400 block truncate">{p.path}</span>
                    {p.recommended && <span className="text-[10px] font-semibold text-brand uppercase">Recommended</span>}
                  </span>
                </label>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client / prospect name (optional)" data-testid="redesign-client-name"
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
              <input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Client email (to send the deck)" data-testid="redesign-client-email"
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes shown on the cover (optional)" data-testid="redesign-notes"
              className="w-full mt-3 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />

            <button onClick={generate} disabled={generating || !selectedUrls.length} data-testid="redesign-generate-btn"
              className="mt-4 bg-brand text-white font-semibold rounded-lg px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Generate redesign deck ({selectedUrls.length} pages)
            </button>
          </div>
        )}

        {/* Decks list */}
        <h2 className="font-heading text-lg font-bold text-slate-900 mt-10 mb-3">Generated Decks</h2>
        {jobs === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand" size={22} /></div>
        ) : jobs.length === 0 ? (
          <p className="text-slate-500 text-center py-12 bg-white border border-slate-200 rounded-2xl">No redesign decks yet. Analyze a site above to create your first one.</p>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="bg-white border border-slate-200 rounded-2xl p-4 card-shadow flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="redesign-job-row">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 truncate">{j.domain}</span>
                    <StatusBadge status={j.status} />
                    {j.avg_score != null && <span className="text-xs text-slate-500">avg {j.avg_score}/100</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {j.total} pages{j.client_name ? ` · ${j.client_name}` : ""}{j.client_email ? ` · ${j.client_email}` : ""}
                    {j.status === "running" ? ` · ${j.progress}/${j.total} done` : ""}
                    {j.emailed_at ? " · emailed ✓" : ""}
                  </p>
                  {j.status === "error" && <p className="text-xs text-rose-500 mt-0.5">{j.error}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {j.status === "done" && (
                    <>
                      <button onClick={() => openPdf(j.id)} data-testid="redesign-view-btn" title="Open PDF"
                        className="inline-flex items-center gap-1.5 text-sm font-medium bg-brand text-white rounded-lg px-3 py-2"><FileText size={14} /> View</button>
                      <button onClick={() => copyLink(j.id)} data-testid="redesign-copylink-btn" title="Copy shareable link"
                        className="text-slate-500 hover:text-brand hover:bg-slate-50 rounded-lg p-2"><Link2 size={16} /></button>
                      <button onClick={() => emailDeck(j.id)} data-testid="redesign-email-btn" title="Email to client"
                        className="text-slate-500 hover:text-brand hover:bg-slate-50 rounded-lg p-2"><Mail size={16} /></button>
                    </>
                  )}
                  <button onClick={() => del(j.id, j.domain)} data-testid="redesign-delete-btn" title="Delete"
                    className="text-rose-400 hover:bg-rose-50 rounded-lg p-2"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
